import React, { useMemo, ReactNode, ComponentType } from 'react';
import { ItemWidthType, LayoutType } from "@om-fe/widgets/dist/components/nav/nav.constants";
import { NavStatic } from '@om-fe/widgets/dist/components/nav/static/nav-static';
import { NavStaticItemModel } from '@om-fe/widgets/dist/components/nav/static/nav-static.constants';
import { WithSelectionBetslipDataModel, withSelectionBetslipData } from '@om-fe/widgets/dist/widgets/betslip/with-betslip-selection-data/with-betslip-selection-data';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { BemMethod, createBem } from '@om-fe/utils';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import Anchor from '@om-fe/core/components/anchor/anchor';
import getConfig from '@om-fe/core/utils/config';

export enum BetslipOpenBetsTabs {
    OpenBets = 'OpenBets',
    BetSlip = 'BetSlip'
}

interface BetslipTabsProps {
    onTabClick: (tab: BetslipOpenBetsTabs) => void;
    selectedTab: BetslipOpenBetsTabs;
    openBetsCount: number | undefined;
    counterLimit?: number;
    closeModal?: () => void;
}

interface BetslipTabsStateProps {
    readonly isAuthenticated: boolean;
    readonly isSmall: boolean;
    pageRouteName: string;
}

const bem: BemMethod = createBem('BetslipTabs');
const betslipOpenBetsBem = createBem('BettingAreaOpenBets');

function BetslipTabsComponent(
    {
        isAuthenticated,
        onTabClick,
        selectedTab,
        openBetsCount,
        selectionList,
        closeModal,
        isSmall,
        pageRouteName
    }: BetslipTabsProps & WithSelectionBetslipDataModel & BetslipTabsStateProps) {
    const { gettext } = useI18NContext();
    const betslipSelectionCount = selectionList?.length || 0;
    const enabledCashoutAble: boolean = getConfig().betslip?.enabledCashoutAbleFilter ?? false;

    const items = useMemo((): NavStaticItemModel<BetslipOpenBetsTabs>[] => [
        {
            id: BetslipOpenBetsTabs.BetSlip,
            text: gettext('Betslip'),
            counter: !!betslipSelectionCount ? betslipSelectionCount : undefined,
            className: bem('OpenBetsCounter'),
        },
        {
            id: BetslipOpenBetsTabs.OpenBets,
            text: gettext('Betting history'),
            counter: openBetsCount,
            counterLimit: enabledCashoutAble ? 99 : undefined,
            className: bem('OpenBetsCounter'),
        },
    ], [betslipSelectionCount, openBetsCount, gettext]);

    const closeButton: ReactNode = RouteNames.betSlip === pageRouteName ? (
        <span
            className={betslipOpenBetsBem('CloseButton BetslipPage')}
        >
            <Anchor
                routeName={RouteNames.home}
                className={betslipOpenBetsBem('CloseButtonLink')}
            >
                <GeneralIcon name="close" />
            </Anchor>
        </span>
    ) : (
        <span
            className={betslipOpenBetsBem('CloseButton')}
            onClick={closeModal}
        >
            <GeneralIcon name="close" />
            <span className={betslipOpenBetsBem('CloseButtonText')}>{gettext('Close')}</span>
        </span>
    );

    if (!isAuthenticated) {
        return (
            <>
                {isSmall && closeButton}
                <NavStatic<BetslipOpenBetsTabs>
                    className={bem()}
                    activeItemId={selectedTab}
                    items={items}
                    hasArrows={false}
                    itemWidthType={ItemWidthType.ItemFullEqualWidth}
                    layoutType={LayoutType.SingleRow}
                    itemType={'BetslipTabs'}
                />
            </>
        );
    }

    return (
        <>
            {isSmall && closeButton}
            <NavStatic<BetslipOpenBetsTabs>
                className={bem()}
                activeItemId={selectedTab}
                items={items}
                hasArrows={false}
                itemWidthType={ItemWidthType.ItemFullEqualWidth}
                layoutType={LayoutType.SingleRow}
                onActiveItemChange={onTabClick}
                itemType={'BetslipTabs'}
            />
        </>
    );
}

export const BetslipTabs: ComponentType<BetslipTabsProps> = compose<ComponentType<BetslipTabsProps>>(
    withSelectionBetslipData<BetslipTabsProps>(),
    connect((state: AppState) => {
        return {
            isAuthenticated: !!state.session?.info?.isAuthenticated,
            isSmall: !!state.breakpoints.small,
            pageRouteName: state.routing.name
        };
    }),
)(BetslipTabsComponent);
